import { createRoot } from 'react-dom/client';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-nz';
import { LicenseInfo } from '@mui/x-license';

import { ListingFilterProvider, PortalFramework, Settings } from '@sal/portal';
import Routes from './Routes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import logo from './branding/brands/sandfield/sandfield_logo.png';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/blinker/300.css';
import '@fontsource/blinker/400.css';
import '@fontsource/blinker/600.css';
import '@fontsource/blinker/700.css';
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/600.css';
import '@fontsource/dm-sans/700.css';
import { BrandProvider } from 'code/context/BrandContext';
import './i18n';
import { SnackbarProvider } from './code/context/SnackbarContext';
import { UnsavedChangesProvider } from './code/context/UnsavedChangesContext';
import { installAccessDeniedInterceptor } from './code/apiAccessInterceptor';

installAccessDeniedInterceptor();

const pfSettings: Settings = {
	logoUrl: logo,
	loginTitle: 'Welcome to the Portal',
	defaultColorMode: 'system',
	useAdapterDayjs: true,
};

LicenseInfo.setLicenseKey(process.env.MUI_X_PRO_LICENSE_KEY || '');

dayjs.extend(updateLocale);
dayjs.updateLocale('en-nz', {
	weekStart: 1,
});
dayjs.locale('en-nz');

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5, // become stale data after 5 minutes, TODO: move to config
			gcTime: 1000 * 60 * 15, // data become refresh when active but cache time run out(While using data from that query, unless enable: false)
			refetchOnWindowFocus: false,
		},
	},
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');
const root = createRoot(container);

root.render(
	<PortalFramework settings={pfSettings}>
		<QueryClientProvider client={queryClient}>
			<LocalizationProvider
				dateAdapter={AdapterDayjs}
				adapterLocale="en-nz"
			>
				<ListingFilterProvider>
					<BrandProvider>
						<SnackbarProvider>
							<UnsavedChangesProvider>
								<Routes />
							</UnsavedChangesProvider>
						</SnackbarProvider>
					</BrandProvider>
				</ListingFilterProvider>
			</LocalizationProvider>
		</QueryClientProvider>
	</PortalFramework>,
);
